import { Fragment, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import axiosAdmin from "../../../../../api/apiAdmin";
import { successToast, errorToast } from "../../../toastAlerts/ToastAlerts";
import {
  BsX,
  BsTruck,
  BsArrowClockwise,
  BsExclamationTriangle,
  BsBoxSeam,
} from "react-icons/bs";
import { apiError } from "../../../../../api/apiError";
import OnWayLabels from "./OnWayLabels";
import { AdminPrimaryContext } from "../../../adminContext/AdminContext";

/**
 * Bulk OnWay dispatch for arrived (status 3) packages.
 *
 * The backend groups packs per (user, delivery address) so a customer with
 * several parcels gets ONE courier delivery with the weights added up — the
 * list below shows groups, not individual packs.
 *
 * Only packs that are declared AND have a resolvable delivery address are
 * offered; anything already on a live OnWay order is filtered out server-side,
 * so a parcel can't be double-booked.
 *
 * "Send all" posts `all: true` with the current filters, so the whole filtered
 * set goes out in one request — no need to tick 500 boxes.
 */
/** Tracking numbers in a group, in the order the parcels were grouped. */
const trackingsOf = (g) =>
  (g.packs || []).map((p) => p.tracking_number).filter(Boolean);

const OnWayBulkDispatch = ({ onClose, abroadOfficeId, shipmentCode, onDone }) => {
  const { t } = useTranslation();
  const { foreignAdresses } = useContext(AdminPrimaryContext);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [checked, setChecked] = useState({}); // key -> bool
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [report, setReport] = useState(null);
  // Unpaid parcels are hidden by default and can only go out with COD on.
  const [includeUnpaid, setIncludeUnpaid] = useState(false);
  const [cod, setCod] = useState(false);
  // Undeclared parcels are hidden too, and that was invisible: a parcel that
  // was both unpaid AND undeclared stayed missing even with the unpaid box
  // ticked, with nothing on screen to say why.
  const [includeUndeclared, setIncludeUndeclared] = useState(false);
  // Order ids to print labels for — set straight after a dispatch, which is
  // when the operator actually needs the stickers.
  const [labelIds, setLabelIds] = useState(null);
  // How many separate packages each group physically leaves in. OnWay's
  // `quantity` counts boxes the courier carries, not our parcels inside them —
  // and we normally repack a customer's parcels into one.
  const [quantities, setQuantities] = useState({}); // key -> number
  // Warehouses and flights are multi-select and seeded from the page, so the
  // dispatch can open with nothing selected and be narrowed to any combination
  // in here. Empty = no restriction.
  const [offices, setOffices] = useState(abroadOfficeId ? [Number(abroadOfficeId)] : []);
  const [flights, setFlights] = useState(shipmentCode ? [shipmentCode] : []);
  // Kept across reloads so the checkbox lists don't blink away while loading.
  const [facets, setFacets] = useState({ abroad_offices: [], shipments: [] });
  // Pickup terminal for the ticked flights. All three gates are around the
  // airport, but the courier still needs the right one.
  const [terminals, setTerminals] = useState([]);
  const [terminal, setTerminal] = useState("");
  const [assigning, setAssigning] = useState(false);

  const filters = useMemo(
    () => ({
      declared: !includeUndeclared,
      with_address: true,
      paid: !includeUnpaid,
      ...(offices.length ? { abroad_office_id: offices } : {}),
      ...(flights.length ? { shipment_code: flights } : {}),
      ...(search ? { search } : {}),
    }),
    // Joined, so a re-render with an equal-but-new array doesn't refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [offices.join(","), flights.join(","), search, includeUnpaid, includeUndeclared]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axiosAdmin.get("/onway/appeared", { params: filters });
      setData(data);
      if (data?.facets) setFacets(data.facets);
      setChecked({});
    } catch (e) {
      errorToast(apiError(e, e?.response?.data?.error || t("admin_error_try_again")));
    }
    setLoading(false);
  }, [filters, t]);

  useEffect(() => {
    load();
  }, [load]);

  // A flight belongs to one warehouse, so unticking a warehouse leaves any of
  // its flights ticked but unmatchable — drop those rather than silently
  // returning nothing.
  useEffect(() => {
    if (!flights.length || !facets.shipments?.length) return;
    const offered = new Set(facets.shipments.map((f) => f.code));
    const kept = flights.filter((c) => offered.has(c));
    if (kept.length !== flights.length) setFlights(kept);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facets.shipments]);

  const toggleIn = (list, setList, value) =>
    setList(
      list.includes(value) ? list.filter((v) => v !== value) : [...list, value]
    );

  useEffect(() => {
    axiosAdmin
      .get("/onway/options")
      .then(({ data }) => {
        setTerminals(data?.terminals || []);
        setTerminal((prev) => prev || data?.default_terminal || "");
      })
      .catch(() => {});
  }, []);

  // Attach one terminal to every ticked flight in a single request.
  const assignTerminal = async () => {
    if (!flights.length) return;
    setAssigning(true);
    try {
      const { data } = await axiosAdmin.post("/shipments/terminal-bulk", {
        codes: flights,
        terminal,
      });
      successToast(
        `${data.updated.length} რეისი — აღება: ${data.label}` +
          (data.missing?.length ? ` (ვერ მოიძებნა: ${data.missing.join(", ")})` : "")
      );
      await load();
      onDone && onDone();
    } catch (e) {
      errorToast(apiError(e, t("admin_error_try_again")));
    }
    setAssigning(false);
  };

  const groups = data?.groups || [];
  const selectedKeys = Object.keys(checked).filter((k) => checked[k]);
  const allChecked = groups.length > 0 && selectedKeys.length === groups.length;

  const toggleAll = () => {
    if (allChecked) return setChecked({});
    const next = {};
    groups.forEach((g) => (next[g.key] = true));
    setChecked(next);
  };

  const selectedTotals = useMemo(() => {
    const sel = groups.filter((g) => checked[g.key]);
    return {
      groups: sel.length,
      packs: sel.reduce((s, g) => s + g.pack_count, 0),
      weight: sel.reduce((s, g) => s + Number(g.weight || 0), 0).toFixed(2),
    };
  }, [groups, checked]);

  const dispatch = async (all) => {
    const count = all ? data?.group_count : selectedTotals.groups;
    const packs = all ? data?.pack_count : selectedTotals.packs;
    if (!count) return;

    if (
      !window.confirm(
        `OnWay-ს გაეგზავნება ${count} შეკვეთა (${packs} ამანათი). გავაგრძელოთ?`
      )
    )
      return;

    setSending(true);
    setReport(null);
    try {
      // Only the entries the operator actually changed; the server treats a
      // missing key as one package.
      const qty = Object.fromEntries(
        Object.entries(quantities).filter(([, v]) => Number(v) > 1)
      );

      const body = all
        ? { ...filters, all: true, cod, quantities: qty }
        : { ...filters, keys: selectedKeys, cod, quantities: qty };
      const { data: res } = await axiosAdmin.post("/onway/appeared/dispatch", body);
      setReport(res);
      if (res.sent_count) {
        const fromBalance = (res.sent || []).reduce((a, x) => a + Number(x.balance_charged || 0), 0);
        successToast(
          `გაიგზავნა ${res.sent_count} შეკვეთა` +
            (fromBalance > 0 ? ` · ბალანსიდან ჩამოიჭრა ${fromBalance.toFixed(2)} ₾` : "")
        );
      }
      if (res.failed_count) errorToast(`ვერ გაიგზავნა ${res.failed_count}`);
      await load();
      onDone && onDone();
    } catch (e) {
      const d = e?.response?.data;
      errorToast(apiError(e, d?.errors?.join(" · ") || d?.error || t("admin_error_try_again")));
    }
    setSending(false);
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[1000] flex items-center justify-center p-3"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-5xl max-h-[92vh] overflow-y-auto p-5"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <BsTruck className="text-2xl text-teal_600" />
            <h2 className="font-bold dark:text-white text-lg">
              OnWay — მასობრივი გაგზავნა
            </h2>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-red-500">
            <BsX className="text-2xl" />
          </button>
        </div>

        {includeUnpaid && (
          <div className="rounded-md bg-amber-50 border border-amber-200 p-3 mb-3 text-xs text-amber-800">
            <b>ყურადღება:</b> გადაუხდელი ამანათები ჩანს. გაგზავნა მხოლოდ COD-ით
            შეიძლება — მონიშნეთ „COD“, თორემ ჯგუფი უარყოფილი იქნება.
            {cod && (
              <div className="mt-1">
                COD-ის რეჟიმში OnWay თრექინგს არ აბრუნებს, ამიტომ ასეთი შეკვეთის
                გაუქმება ჩვენი მხრიდან შეუძლებელია — ჯერ სატესტო რეჟიმშია.
              </div>
            )}
          </div>
        )}
        <p className="text-xs text-slate-500 mb-3">
          ჩამონათვალში მხოლოდ დეკლარირებული და მიტანის მისამართის მქონე ამანათებია.
          ერთი მომხმარებლის რამდენიმე ამანათი ერთ შეკვეთად ერთიანდება (წონა ჯამდება).
          „შეფუთვა" — რამდენ ცალკე ყუთს გაიტანს კურიერი; ნაგულისხმევად 1, რადგან
          ამანათებს ერთად ვფუთავთ.
        </p>

        {/* Toolbar */}
        <div className="flex gap-2 flex-wrap items-center mb-3">
          <input
            className="h-9 px-3 border border-slate-300 dark:border-slate-600 rounded-md
                       dark:bg-slate-700 dark:text-white text-sm flex-1 min-w-[200px]"
            placeholder="ძებნა: იუზერ ID ან თრექინგი (ზუსტი)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button
            onClick={load}
            className="h-9 px-3 rounded-md bg-slate-200 dark:bg-slate-600 dark:text-white text-sm"
            title="განახლება"
          >
            <BsArrowClockwise />
          </button>

        </div>

        {/* Warehouse + flight filters. Both optional and multi-select: nothing
            ticked means no restriction. Counts are dispatchable packs, and a
            flight only appears at all if it still has one. */}
        <div className="grid grid-cols-2 md:grid-cols-1 gap-3 mb-3">
          <div className="border border-slate-200 dark:border-slate-600 rounded-md p-2 self-start w-full
                          h-[150px] overflow-auto">
            <div className="flex items-center justify-between mb-1 sticky top-0
                            bg-white dark:bg-slate-800 pb-1">
              <span className="text-xs font-bold text-slate-500 dark:text-slate-300">
                საწყობი
                {offices.length > 0 && (
                  <span className="text-teal_600"> · მონიშნული {offices.length}</span>
                )}
              </span>
              {offices.length > 0 && (
                <button
                  onClick={() => setOffices([])}
                  className="text-[11px] text-teal_600 hover:underline"
                >
                  გასუფთავება
                </button>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              {facets.abroad_offices?.length ? (
                facets.abroad_offices.map((o) => (
                  <label
                    key={o.id}
                    className="flex items-center gap-2 text-[11px] leading-tight
                               dark:text-slate-200 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 shrink-0"
                      checked={offices.includes(o.id)}
                      onChange={() => toggleIn(offices, setOffices, o.id)}
                    />
                    <span className="flex-1 truncate" title={o.name}>
                      {o.name}
                    </span>
                    <b className="text-teal_600">{o.packs}</b>
                  </label>
                ))
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </div>
          </div>

          <div className="border border-slate-200 dark:border-slate-600 rounded-md p-2 self-start w-full
                          h-[150px] overflow-auto">
            <div className="flex items-center justify-between mb-1 sticky top-0
                            bg-white dark:bg-slate-800 pb-1">
              <span className="text-xs font-bold text-slate-500 dark:text-slate-300">
                რეისი ({facets.shipments?.length || 0})
                {flights.length > 0 && (
                  <span className="text-teal_600"> · მონიშნული {flights.length}</span>
                )}
              </span>
              {flights.length > 0 && (
                <button
                  onClick={() => setFlights([])}
                  className="text-[11px] text-teal_600 hover:underline"
                >
                  გასუფთავება
                </button>
              )}
            </div>
            <div className="flex flex-col gap-0.5">
              {facets.shipments?.length ? (
                facets.shipments.map((f) => (
                  <label
                    key={f.code}
                    className="flex items-center gap-2 text-[11px] leading-tight
                               dark:text-slate-200 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      className="w-3.5 h-3.5 shrink-0"
                      checked={flights.includes(f.code)}
                      onChange={() => toggleIn(flights, setFlights, f.code)}
                    />
                    <span className="flex-1 font-mono truncate">{f.code}</span>
                    <span
                      className={`text-[10px] ${
                        f.terminal_set ? "text-slate-500" : "text-slate-400 italic"
                      }`}
                      title={
                        f.terminal_set
                          ? "მიბმული ტერმინალი"
                          : "ნაგულისხმევი — ტერმინალი მიბმული არ არის"
                      }
                    >
                      {f.terminal_label}
                    </span>
                    <b className="text-teal_600">{f.packs}</b>
                  </label>
                ))
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </div>

          </div>
        </div>

        {/* Assign the pickup terminal to whatever is ticked above. */}
        <div className="flex items-center gap-2 mb-3">
        <select
          className="h-8 px-2 border border-slate-300 dark:border-slate-600 rounded-md
                     bg-white dark:bg-slate-700 text-slate-800 dark:text-white text-xs"
          value={terminal}
          onChange={(e) => setTerminal(e.target.value)}
        >
          {terminals.map((tr) => (
            <option key={tr.key} value={tr.key}>
              {tr.label}
            </option>
          ))}
        </select>
        <button
          onClick={assignTerminal}
          disabled={assigning || !flights.length}
          title={
            flights.length
              ? `მიება ${flights.length} რეისს`
              : "მონიშნეთ რეისი ზემოთ"
          }
          className={`h-8 px-3 rounded-md text-white text-xs font-bold ${
            flights.length
              ? "bg-teal_600 hover:opacity-90"
              : "bg-slate-400 cursor-not-allowed"
          }`}
        >
          {assigning
            ? "..."
            : `აღების ტერმინალის მიბმა${
                flights.length ? ` (${flights.length})` : ""
              }`}
        </button>
        </div>

        <div className="flex gap-2 flex-wrap items-center mb-3">
          <label className="flex items-center gap-2 text-xs dark:text-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={includeUnpaid}
              onChange={(e) => {
                setIncludeUnpaid(e.target.checked);
                if (!e.target.checked) setCod(false);
              }}
            />
            გადაუხდელების ჩვენება
          </label>

          {includeUnpaid && (
            <label className="flex items-center gap-2 text-xs text-amber-700 cursor-pointer">
              <input
                type="checkbox"
                checked={cod}
                onChange={(e) => setCod(e.target.checked)}
              />
              COD — თანხა კურიერთან აიღება
            </label>
          )}

          <label className="flex items-center gap-2 text-xs dark:text-slate-200 cursor-pointer">
            <input
              type="checkbox"
              checked={includeUndeclared}
              onChange={(e) => setIncludeUndeclared(e.target.checked)}
            />
            არადეკლარირებულების ჩვენება
          </label>
        </div>

        {/* Say what the filters are holding back, so a missing parcel is
            explained rather than simply absent. */}
        {!includeUndeclared && data?.hidden_undeclared > 0 && (
          <div className="mb-3 text-xs text-amber-700 bg-amber-50 dark:bg-amber-900/20
            border border-amber-200 dark:border-amber-800 rounded-md px-3 py-2">
            კიდევ <b>{data.hidden_undeclared}</b> ამანათი ემთხვევა ფილტრს, მაგრამ
            დამალულია — არადეკლარირებულია (დეკლარაცია ან ნივთების სია არ აქვს).
            მოსანიშნად ჩართეთ „არადეკლარირებულების ჩვენება“.
          </div>
        )}

        {loading ? (
          <div className="animate-pulse h-56 bg-gray-100 dark:bg-slate-700 rounded-md" />
        ) : (
          <>
            <div className="flex flex-wrap gap-4 text-sm mb-3 dark:text-slate-200">
              <span>
                შეკვეთა: <b>{data?.group_count ?? 0}</b>
              </span>
              <span>
                ამანათი: <b>{data?.pack_count ?? 0}</b>
              </span>
              {data?.skipped_no_address > 0 && (
                <span className="text-amber-600">
                  მისამართის გარეშე გამოტოვებული: <b>{data.skipped_no_address}</b>
                </span>
              )}
            </div>

            {groups.length === 0 ? (
              <p className="text-sm text-slate-500 py-6 text-center">
                გასაგზავნი ამანათი არ არის — ან დეკლარირებული არ არის, ან მისამართი
                არ აქვს, ან უკვე გაგზავნილია.
              </p>
            ) : (
              <div className="overflow-x-auto border border-slate-200 dark:border-slate-600 rounded-md">
                <table className="w-full text-xs">
                  <thead className="text-slate-500 bg-slate-50 dark:bg-slate-700">
                    <tr>
                      <th className="p-2 w-8">
                        <input
                          type="checkbox"
                          checked={allChecked}
                          onChange={toggleAll}
                          title="ყველას მონიშვნა"
                        />
                      </th>
                      <th className="text-left p-2">მომხმარებელი</th>
                      <th className="text-left p-2">თრექინგი</th>
                      <th className="text-left p-2">მისამართი</th>
                      <th className="text-right p-2">ამანათი</th>
                      <th className="text-center p-2">შეფუთვა</th>
                      <th className="text-right p-2">წონა</th>
                      <th className="text-right p-2">გადასახდელი</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {groups.map((g) => (
                      <Fragment key={g.key}>
                        <tr className="border-t border-slate-100 dark:border-slate-700">
                          <td className="p-2">
                            <input
                              type="checkbox"
                              checked={!!checked[g.key]}
                              onChange={() =>
                                setChecked((p) => ({ ...p, [g.key]: !p[g.key] }))
                              }
                            />
                          </td>
                          <td className="p-2 dark:text-slate-200">
                            <span className="font-semibold">
                              {g.user_name || "—"}
                            </span>{" "}
                            <span
                              className="font-mono text-teal_600"
                              title={`იუზერ ID: ${g.user_id}`}
                            >
                              #{g.user_id}
                            </span>
                            <div className="text-slate-400">
                              {g.address_phone || g.user_phone || "—"}
                            </div>
                          </td>

                          {/* Only the first two fit on a row; the rest are on
                              hover so the table stays one line per order. */}
                          <td
                            className="p-2 font-mono text-[11px] dark:text-slate-200
                                       align-top max-w-[190px]"
                            title={trackingsOf(g).join("\n")}
                          >
                            {trackingsOf(g).slice(0, 2).map((tr) => (
                              <div key={tr}>{tr}</div>
                            ))}
                            {trackingsOf(g).length > 2 && (
                              <div className="text-teal_600 cursor-help">
                                +{trackingsOf(g).length - 2} სხვა
                              </div>
                            )}
                            {trackingsOf(g).length === 0 && (
                              <span className="text-slate-400">—</span>
                            )}
                          </td>

                          <td className="p-2 dark:text-slate-200">
                            {g.city} — {g.address}
                          </td>
                          <td className="p-2 text-right dark:text-slate-200">
                            {g.pack_count > 1 ? (
                              <span className="font-bold text-teal_600">
                                {g.pack_count} ✕
                              </span>
                            ) : (
                              g.pack_count
                            )}
                          </td>

                          {/* Packages the courier carries. One by default: the
                              parcels above normally travel inside a single box. */}
                          <td className="p-2 text-center">
                            <input
                              type="number"
                              min="1"
                              max="99"
                              value={quantities[g.key] ?? 1}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                setQuantities((p) => ({
                                  ...p,
                                  [g.key]: Math.max(
                                    1,
                                    Math.min(99, Number(e.target.value) || 1)
                                  ),
                                }))
                              }
                              className="w-12 h-7 px-1 text-center border border-slate-300
                                         dark:border-slate-600 rounded
                                         dark:bg-slate-800 dark:text-white"
                              title="რამდენ ცალკე შეფუთვად მიდის — ნაგულისხმევი 1"
                            />
                          </td>
                          <td className="p-2 text-right dark:text-slate-200">
                            {g.weight} კგ
                          </td>
                          <td className="p-2 text-right">
                            {Number(g.unpaid) > 0 && g.balance_covers ? (
                              // Paid from the balance at dispatch — no COD.
                              <span
                                className="text-teal-700 font-bold"
                                title={`ბალანსზე ${Number(g.balance).toFixed(2)} ₾ — გაგზავნისას ჩამოიჭრება`}
                              >
                                {Number(g.unpaid).toFixed(2)} ₾ ბალანსიდან
                              </span>
                            ) : Number(g.unpaid) > 0 ? (
                              <span className="text-amber-700 font-bold">
                                {Number(g.unpaid).toFixed(2)} ₾
                              </span>
                            ) : (
                              <span className="text-green-600">გადახდილი</span>
                            )}
                          </td>
                          <td className="p-2">
                            <button
                              onClick={() =>
                                setExpanded(expanded === g.key ? null : g.key)
                              }
                              className="text-slate-500 hover:text-teal_600"
                              title="ამანათების ნახვა"
                            >
                              <BsBoxSeam />
                            </button>
                          </td>
                        </tr>
                        {expanded === g.key && (
                          <tr>
                            <td colSpan={9} className="p-2 bg-slate-50 dark:bg-slate-700">
                              <div className="flex flex-wrap gap-2">
                                {g.packs.map((p) => (
                                  <span
                                    key={p.id}
                                    className="text-[11px] font-mono bg-white dark:bg-slate-800
                                               dark:text-slate-200 border border-slate-200
                                               dark:border-slate-600 rounded px-2 py-1"
                                  >
                                    {p.tracking_number} · {p.chargeable}კგ
                                  </span>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Per-group outcome of the last dispatch */}
            {report && (
              <div className="mt-3 text-xs">
                {report.failed_count > 0 && (
                  <div className="rounded-md bg-red-50 border border-red-200 p-3 mb-2">
                    <p className="font-bold text-red-700 mb-1">
                      <BsExclamationTriangle className="inline" /> ვერ გაიგზავნა (
                      {report.failed_count}):
                    </p>
                    {report.failed.map((f, i) => (
                      <p key={i} className="text-red-700">
                        {f.key} — {f.error}
                      </p>
                    ))}
                  </div>
                )}
                {report.sent_count > 0 && (
                  <div className="rounded-md bg-green-50 border border-green-200 p-3">
                    <div className="flex items-center justify-between mb-1 sticky top-0
                            bg-white dark:bg-slate-800 pb-1">
                      <p className="font-bold text-green-700">
                        გაიგზავნა ({report.sent_count}):
                      </p>
                      <button
                        onClick={() =>
                          setLabelIds(report.sent.map((s) => s.order_id).filter(Boolean))
                        }
                        className="rounded-md bg-teal_600 text-white text-[11px] font-bold px-3 h-7"
                      >
                        ეტიკეტების ბეჭდვა
                      </button>
                    </div>
                    {report.sent.map((s, i) => (
                      <p key={i} className="text-green-700">
                        {s.tracking} — {s.pack_count} ამანათი, {s.weight} კგ
                      </p>
                    ))}
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end mt-4 flex-wrap">
              <button
                onClick={onClose}
                className="rounded-full bg-gray-200 text-slate-700 text-sm font-bold px-5 py-2"
              >
                დახურვა
              </button>
              <button
                onClick={() => dispatch(false)}
                disabled={sending || selectedTotals.groups === 0}
                className="rounded-full bg-teal_600 text-white text-sm font-bold px-5 py-2
                           hover:opacity-90 disabled:opacity-40"
              >
                {sending
                  ? "იგზავნება…"
                  : `მონიშნულის გაგზავნა (${selectedTotals.groups} შეკვეთა / ${selectedTotals.packs} ამანათი)`}
              </button>
              <button
                onClick={() => dispatch(true)}
                disabled={sending || !data?.group_count}
                className="rounded-full bg-red-500 text-white text-sm font-bold px-5 py-2
                           hover:opacity-90 disabled:opacity-40"
                title="მონიშვნის გარეშე — ყველა გაფილტრული ამანათი"
              >
                ყველას გაგზავნა ({data?.group_count ?? 0})
              </button>
            </div>
          </>
        )}
      </div>

      {labelIds && (
        <OnWayLabels orderIds={labelIds} onClose={() => setLabelIds(null)} />
      )}
    </div>
  );
};

export default OnWayBulkDispatch;
